/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { SIDEBAR_TYPE, SIDEBAR_MODE } from '../constants/types';

export const ContextMenuContext = createContext(null);

/**
 * Provider for context menu state - manages right-click menu for day cells
 */
export function ContextMenuProvider({ children, openSidebar, selectionStart, selectionEnd }) {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuDate, setContextMenuDate] = useState(null);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  
  const handleDayClick = useCallback((date, e) => {
    if (!selectionStart || !selectionEnd) return;
    
    const isSingleDay = selectionStart.getTime() === selectionEnd.getTime();
    if (isSingleDay && selectionStart.getTime() === date.getTime()) {
      setContextMenuDate(date);
      setContextMenuPosition({ x: e.clientX, y: e.clientY });
      setShowContextMenu(true);
    }
  }, [selectionStart, selectionEnd]);
  
  const handleAddEventFromMenu = useCallback(() => {
    if (contextMenuDate && openSidebar) {
      openSidebar(SIDEBAR_TYPE.EVENT, SIDEBAR_MODE.CREATE, { startDate: contextMenuDate, endDate: contextMenuDate });
    }
  }, [contextMenuDate, openSidebar]);
  
  const handleAddMilestoneFromMenu = useCallback(() => {
    if (contextMenuDate && openSidebar) {
      openSidebar(SIDEBAR_TYPE.MILESTONE, SIDEBAR_MODE.CREATE, { date: contextMenuDate });
    }
  }, [contextMenuDate, openSidebar]);
  
  const value = useMemo(() => ({
    showContextMenu,
    contextMenuDate,
    contextMenuPosition,
    setShowContextMenu,
    onDayClick: handleDayClick,
    onAddEventFromMenu: handleAddEventFromMenu,
    onAddMilestoneFromMenu: handleAddMilestoneFromMenu,
  }), [
    showContextMenu,
    contextMenuDate,
    contextMenuPosition,
    handleDayClick,
    handleAddEventFromMenu,
    handleAddMilestoneFromMenu,
  ]);
  
  return (
    <ContextMenuContext.Provider value={value}>
      {children}
    </ContextMenuContext.Provider>
  );
}

/**
 * Hook to access context menu context
 */
export function useContextMenuContext() {
  const context = useContext(ContextMenuContext);
  if (!context) {
    throw new Error('useContextMenuContext must be used within ContextMenuProvider');
  }
  return context;
}
